import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, CircularProgress, Typography } from '@mui/material';
import { exchangeCodeForTokens, getLoginUrl } from '../services/auth';
import { useAuth } from '../contexts/AuthContext';

export default function AuthCallback() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { refreshProfile } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const exchanged = useRef(false);

  useEffect(() => {
    // Prevent double exchange in React StrictMode
    if (exchanged.current) return;
    exchanged.current = true;

    // Cognito redirects back with `error`/`error_description` instead of `code`
    // when the Hosted UI or a Lambda trigger (e.g. PreSignUp) rejects the flow —
    // surface that real reason instead of a generic "no code" message.
    const oauthError = searchParams.get('error_description') || searchParams.get('error');
    if (oauthError) {
      setError(oauthError);
      return;
    }

    const code = searchParams.get('code');
    if (!code) {
      setError('No authorization code received');
      return;
    }

    exchangeCodeForTokens(code)
      .then(() => refreshProfile())
      .then(() => navigate('/', { replace: true }))
      .catch((err) => setError(err.message || 'Authentication failed. Please try again.'));
  }, [searchParams, navigate, refreshProfile]);

  if (error) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
        <Typography color="error">{error}</Typography>
        <Typography variant="body2" color="text.secondary">
          <a href={getLoginUrl()} style={{ color: '#7C3AED' }}>Retour a la connexion</a>
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
      <CircularProgress size={24} />
      <Typography>Connexion en cours...</Typography>
    </Box>
  );
}
